import "animate.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "../App.css";
import { useState, useEffect, useMemo, useRef } from "react";
import { ref, onValue } from "firebase/database";
import { db } from "../firebase.js";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { addItem } from "../store/cartSlice.js";
import { fetchProducts } from "../store/productsSlice.js";
import SpecialOfferPopup from "./SpecialOfferPopup.js";
import SpecialOffersCarousel from "./SpecialOffersCarousel.js";

const CategoryRow = ({ category, items }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((s) => s.auth.user);
  const rowRef = useRef(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeftRef = useRef(0);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(true);

  const updateArrows = () => {
    const el = rowRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 10);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 10);
  };

  useEffect(() => {
    const el = rowRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateArrows, { passive: true });
    updateArrows();
    return () => el.removeEventListener("scroll", updateArrows);
  }, [items]);

  const onMouseDown = (e) => {
    isDragging.current = true;
    startX.current = e.pageX - rowRef.current.offsetLeft;
    scrollLeftRef.current = rowRef.current.scrollLeft;
    rowRef.current.style.cursor = "grabbing";
  };
  const stopDrag = () => { isDragging.current = false; if (rowRef.current) rowRef.current.style.cursor = "grab"; };
  const onMouseMove = (e) => {
    if (!isDragging.current) return;
    e.preventDefault();
    const x = e.pageX - rowRef.current.offsetLeft;
    rowRef.current.scrollLeft = scrollLeftRef.current - (x - startX.current) * 1.5;
  };

  const scroll = (dir) => rowRef.current.scrollBy({ left: dir * 320, behavior: "smooth" });

  const handleAddToCart = (item, e) => {
    e.stopPropagation();
    if (!user) { toast.info("Please sign in to purchase"); navigate("/signin"); return; }
    dispatch(addItem({ id: item.id, name: item.name, price: item.price, image: item.images?.[0] || item.image }));
    toast.success("Added to cart!");
  };

  return (
    <div className="cat-section mb-5">
      <div className="cat-heading mb-3">
        <span className="cat-accent-bar"></span>
        <h4 className="cat-title mb-0">{category}</h4>
        <span className="cat-count">{items.length} products</span>
      </div>

      <div className="cat-row-wrap">
        <button
          className={`cat-float-btn cat-float-left${canLeft ? " visible" : ""}`}
          onClick={() => scroll(-1)}
          aria-label="scroll left"
        >
          <i className="bi bi-chevron-left"></i>
        </button>

        <div
          ref={rowRef}
          onMouseDown={onMouseDown}
          onMouseLeave={stopDrag}
          onMouseUp={stopDrag}
          onMouseMove={onMouseMove}
          className="cat-row category-row"
        >
          {items.map((item) => {
            const discount = item.oldPrice ? Math.round(((item.oldPrice - item.price) / item.oldPrice) * 100) : 0;
            return (
              <div key={item.id} className="cat-card-wrap" onClick={() => navigate(`/product/${item.id}`)}>
                <div className="cat-card">
                  <div className="cat-img-wrap">
                    <img
                      src={item.images?.[0] || item.image || "/slider/l1.jpeg"}
                      alt={item.name}
                      className="cat-img"
                      draggable={false}
                    />
                    {discount > 0 && <span className="cat-badge">{discount}% OFF</span>}
                    <div className="cat-overlay">
                      <button className="cat-overlay-btn" onClick={(e) => handleAddToCart(item, e)}>
                        <i className="bi bi-cart-plus me-1"></i> Add to Cart
                      </button>
                      <button className="cat-overlay-buy" onClick={(e) => { e.stopPropagation(); handleAddToCart(item, e); navigate("/cart"); }}>
                        <i className="bi bi-lightning-fill me-1"></i> Buy Now
                      </button>
                    </div>
                  </div>
                  <div className="cat-body">
                    <p className="cat-name">{item.name}</p>
                    <div className="cat-price-row">
                      <span className="cat-price">₹{item.price?.toLocaleString()}</span>
                      {item.oldPrice && <span className="cat-old-price">₹{item.oldPrice?.toLocaleString()}</span>}
                    </div>
                    <div className="cat-mobile-actions">
                      <button className="cat-mobile-cart" onClick={(e) => handleAddToCart(item, e)}>
                        <i className="bi bi-cart-plus"></i> Cart
                      </button>
                      <button className="cat-mobile-buy" onClick={(e) => { e.stopPropagation(); handleAddToCart(item, e); navigate("/cart"); }}>
                        <i className="bi bi-lightning-fill"></i> Buy
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <button
          className={`cat-float-btn cat-float-right${canRight ? " visible" : ""}`}
          onClick={() => scroll(1)}
          aria-label="scroll right"
        >
          <i className="bi bi-chevron-right"></i>
        </button>
      </div>
    </div>
  );
};

const HeroSlider = () => {
  const [slides, setSlides] = useState([]);
  const [slidesLoaded, setSlidesLoaded] = useState(false);
  const [activeFilter, setActiveFilter] = useState("All");
  const dispatch = useDispatch();
  const products = useSelector((s) => s.products.items || []);

  useEffect(() => { dispatch(fetchProducts()); }, [dispatch]);

  useEffect(() => {
    if (!db) return;
    const unsub = onValue(ref(db, "banners"), (snapshot) => {
      if (snapshot.exists()) {
        const val = snapshot.val();
        const list = Object.keys(val).map((k) => ({ id: k, ...val[k] }));
        list.sort((a, b) => (b.isFirst ? 1 : 0) - (a.isFirst ? 1 : 0));
        setSlides(list);
      } else {
        setSlides([]);
      }
      setSlidesLoaded(true);
    }, () => { setSlidesLoaded(true); });
    return () => unsub();
  }, []);

  const categories = useMemo(() => {
    const map = {};
    products.forEach((p) => {
      const cat = p.type || "Other";
      if (!map[cat]) map[cat] = [];
      map[cat].push(p);
    });
    return map;
  }, [products]);

  const allCategories = ["All", ...Object.keys(categories)];

  const filteredCategories = useMemo(() => {
    if (activeFilter === "All") return categories;
    return categories[activeFilter] ? { [activeFilter]: categories[activeFilter] } : {};
  }, [categories, activeFilter]);

  return (
    <>
      <style>{`
        .banner-caption {
          position: absolute !important;
          bottom: 20px !important;
          left: 50% !important;
          transform: translateX(-50%) !important;
          width: auto !important;
          max-width: 80% !important;
          background: rgba(0,0,0,0.55) !important;
          color: #fff !important;
          border-radius: 12px !important;
          padding: 14px 28px !important;
          backdrop-filter: blur(8px) !important;
          text-align: center !important;
          z-index: 10 !important;
          border: none !important;
          box-shadow: none !important;
        }
        .banner-caption-title {
          color: #fff !important;
          font-size: 1.6rem !important;
          font-weight: 800 !important;
          margin-bottom: 4px !important;
          text-shadow: 0 2px 8px rgba(0,0,0,0.4) !important;
        }
        .banner-caption-text {
          color: #eee !important;
          font-size: 1rem !important;
          margin: 0 !important;
        }
        @media (max-width: 768px) {
          .banner-caption {
            bottom: 10px !important;
            left: 50% !important;
            transform: translateX(-50%) !important;
            max-width: 90% !important;
            padding: 10px 16px !important;
          }
          .banner-caption-title { font-size: 1.1rem !important; }
          .banner-caption-text { font-size: 0.82rem !important; }
        }
        @media (max-width: 480px) {
          .banner-caption {
            bottom: 6px !important;
            max-width: 95% !important;
            padding: 8px 12px !important;
          }
          .banner-caption-title { font-size: 0.95rem !important; }
          .banner-caption-text { font-size: 0.75rem !important; }
        }
      `}</style>
      {/* Hero Carousel */}
      <div className="position-relative" style={{ marginTop: "80px" }}>
        {slides.length === 0 && !slidesLoaded ? (
          <div className="banner-skeleton"></div>
        ) : slides.length === 0 ? (
          <div className="d-flex align-items-center justify-content-center bg-light" style={{ height: "400px" }}>
            <div className="text-center text-muted">
              <i className="bi bi-image" style={{ fontSize: "3rem" }}></i>
              <p className="mt-2">No banners added yet</p>
            </div>
          </div>
        ) : (
          <div id="heroCarousel" className="carousel slide" data-bs-ride="carousel" data-bs-interval="6500">
            <div className="carousel-inner">
              {slides.map((slide, index) => (
                <div key={index} className={`carousel-item ${index === 0 ? "active" : ""}`}>
                  <div style={{ position: "relative", width: "100%" }}>
                    <img
                      src={slide.image}
                      alt={slide.title}
                      className="d-block w-100"
                      loading={index === 0 ? "eager" : "lazy"}
                      style={{ width: "100%", height: "auto", minHeight: "200px", objectFit: "cover", display: "block", borderRadius: 0 }}
                    />
                    {(slide.title || slide.text) && (
                      <div className={`banner-caption animate__animated ${slide.animation || "animate__fadeInUp"}`}>
                        {slide.title && <h1 className="banner-caption-title text-gradient">{slide.title}</h1>}
                        {slide.text && <p className="banner-caption-text">{slide.text}</p>}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <button className="carousel-control-prev" type="button" data-bs-target="#heroCarousel" data-bs-slide="prev" style={{ width: 60, height: 60, borderRadius: "50%", backgroundColor: "rgba(102,126,234,0.9)", border: "none", left: "10%", top: "50%", transform: "translateY(-50%)" }}>
              <i className="bi bi-chevron-left" style={{ fontSize: "1.5rem", color: "white" }}></i>
            </button>
            <button className="carousel-control-next" type="button" data-bs-target="#heroCarousel" data-bs-slide="next" style={{ width: 60, height: 60, borderRadius: "50%", backgroundColor: "rgba(102,126,234,0.9)", border: "none", right: "10%", top: "50%", transform: "translateY(-50%)" }}>
              <i className="bi bi-chevron-right" style={{ fontSize: "1.5rem", color: "white" }}></i>
            </button>
            <div className="carousel-indicators" style={{ bottom: 10 }}>
              {slides.map((_, index) => (
                <button key={index} type="button" data-bs-target="#heroCarousel" data-bs-slide-to={index} className={index === 0 ? "active" : ""} style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: index === 0 ? "#667eea" : "rgba(102,126,234,0.3)", border: "none", margin: "0 5px" }}></button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Products Section */}
      {Object.keys(categories).length > 0 && (
        <div className="products-section">
          <div className="products-section-header">
            <div>
              <h2 className="products-section-title">Our Collection</h2>
              <p className="products-section-sub">Explore our finest fragrances</p>
            </div>
          </div>

          {/* Filter bar */}
          <div className="filter-bar">
            {allCategories.map((cat) => (
              <button
                key={cat}
                className={`filter-pill${activeFilter === cat ? " active" : ""}`}
                onClick={() => setActiveFilter(cat)}
              >
                {cat}
                {cat !== "All" && <span className="filter-pill-count">{categories[cat]?.length}</span>}
              </button>
            ))}
          </div>

          <div className="container-fluid px-3 px-md-4">
            {Object.entries(filteredCategories).map(([cat, items]) => (
              <CategoryRow key={cat} category={cat} items={items} />
            ))}
          </div>
        </div>
      )}

      <SpecialOffersCarousel />
      <SpecialOfferPopup />
    </>
  );
};

export default HeroSlider;
